import { BigNumber, ethers } from "ethers";
import { gql } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Token as UniToken } from "@uniswap/sdk-core";
import { Pool } from "@uniswap/v3-sdk";
import useSWR from "swr";

import OrderBook from "abis/OrderBook.json";
import PositionManager from "abis/PositionManager.json";
import Vault from "abis/Vault.json";
import Router from "abis/Router.json";
import UniPool from "abis/UniPool.json";
import UniswapV2 from "abis/UniswapV2.json";
import LiquidityLocker from "abis/LiquidityLocker.json";

import Token from "abis/Token.json";
import PositionRouter from "abis/PositionRouter.json";
import ReaderV2 from "abis/ReaderV2.json";

import { getContract } from "config/contracts";
import { ARBITRUM, ARBITRUM_TESTNET, AVALANCHE, BASE, FANTOM, OP, getConstant, getHighExecutionFee } from "config/chains";
import { DECREASE, getOrderKey, INCREASE, SWAP, USD_DECIMALS } from "lib/legacy";

import { groupBy } from "lodash";
import { UI_VERSION } from "config/ui";
import { getServerBaseUrl, getServerUrl } from "config/backend";
import { getGmxGraphClient, nissohGraphClient } from "lib/subgraph/clients";
import { callContract, contractFetcher } from "lib/contracts";
import { replaceNativeTokenAddress } from "./tokens";
import { getUsd } from "./tokens/utils";
import { getProvider } from "lib/rpc";
import { bigNumberify, expandDecimals, parseValue } from "lib/numbers";
import { getTokenBySymbol } from "config/tokens";
import { t } from "@lingui/macro";

export * from "./prices";

const { AddressZero } = ethers.constants;

export function useAllOrdersStats(chainId) {
  const query = gql(`{
    orderStat(id: "total") {
      openSwap
      openIncrease
      openDecrease
      executedSwap
      executedIncrease
      executedDecrease
      cancelledSwap
      cancelledIncrease
      cancelledDecrease
    }
  }`);

  const [res, setRes] = useState<any>();

  useEffect(() => {
    const graphClient = getGmxGraphClient(chainId);
    if (graphClient) {
      // eslint-disable-next-line no-console
      graphClient.query({ query }).then(setRes).catch(console.warn);
    }
  }, [setRes, query, chainId]);

  return res ? res.data.orderStat : null;
}

export function useUserStat(chainId) {
  const query = gql(`{
    userStat(id: "total") {
      id
      uniqueCount
    }
  }`);

  const [res, setRes] = useState<any>();

  useEffect(() => {
    // eslint-disable-next-line no-console
    getGmxGraphClient(chainId)?.query({ query }).then(setRes).catch(console.warn);
  }, [setRes, query, chainId]);

  return res ? res.data.userStat : null;
}

export function useLiquidationsData(chainId, account) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (account) {
      const query = gql(`{
         liquidatedPositions(
           where: {account: "${account.toLowerCase()}"}
           first: 100
           orderBy: timestamp
           orderDirection: desc
         ) {
           key
           timestamp
           borrowFee
           loss
           collateral
           size
           markPrice
           type
         }
      }`);
      const graphClient = getGmxGraphClient(chainId);
      if (!graphClient) {
        return;
      }

      graphClient
        .query({ query })
        .then((res) => {
          const _data = res.data.liquidatedPositions.map((item) => {
            return {
              ...item,
              size: bigNumberify(item.size),
              collateral: bigNumberify(item.collateral),
              markPrice: bigNumberify(item.markPrice),
            };
          });
          setData(_data);
        })
        // eslint-disable-next-line no-console
        .catch(console.warn);
    }
  }, [setData, chainId, account]);

  return data;
}

export function useAllPositions(chainId, library) {
  const count = 1000;
  const query = gql(`{
    aggregatedTradeOpens(
      first: ${count}
    ) {
      account
      initialPosition{
        indexToken
        collateralToken
        isLong
        sizeDelta
      }
      increaseList {
        sizeDelta
      }
      decreaseList {
        sizeDelta
      }
    }
  }`);

  const [res, setRes] = useState<any>();

  useEffect(() => {
    // eslint-disable-next-line no-console
    nissohGraphClient.query({ query }).then(setRes).catch(console.warn);
  }, [setRes, query]);

  const key = res ? `allPositions${count}__` : null;

  const { data: positions = [] } = useSWR(key, async () => {
    const provider = getProvider(library, chainId);
    const vaultAddress = getContract(chainId, "Vault");
    const contract = new ethers.Contract(vaultAddress, Vault.abi, provider);
    const ret = await Promise.all(
      res.data.aggregatedTradeOpens.map(async (dataItem) => {
        try {
          const { indexToken, collateralToken, isLong } = dataItem.initialPosition;
          const positionData = await contract.getPosition(dataItem.account, collateralToken, indexToken, isLong);
          const position: any = {
            size: bigNumberify(positionData[0]),
            collateral: bigNumberify(positionData[1]),
            entryFundingRate: bigNumberify(positionData[3]),
            account: dataItem.account,
          };
          position.fundingFee = await contract.getFundingFee(collateralToken, position.size, position.entryFundingRate);
          position.marginFee = position.size.div(1000);
          position.fee = position.fundingFee.add(position.marginFee);

          const THRESHOLD = 5000;
          const collateralDiffPercent = position.fee.mul(10000).div(position.collateral);
          position.danger = collateralDiffPercent.gt(THRESHOLD);

          return position;
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      })
    );

    return ret.filter(Boolean);
  });

  return positions;
}

export function useAllOrders(chainId, library) {
  const query = gql(`{
    orders(
      first: 1000,
      orderBy: createdTimestamp,
      orderDirection: desc,
      where: {status: "open"}
    ) {
      type
      account
      index
      status
      createdTimestamp
    }
  }`);

  const [res, setRes] = useState<any>();

  useEffect(() => {
    getGmxGraphClient(chainId)?.query({ query }).then(setRes);
  }, [setRes, query, chainId]);

  const key = res ? res.data.orders.map((order) => `${order.type}-${order.account}-${order.index}`) : null;
  const { data: orders = [] } = useSWR(key, () => {
    const provider = getProvider(library, chainId);
    const orderBookAddress = getContract(chainId, "OrderBook");
    const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, provider);
    return Promise.all(
      res.data.orders.map(async (order) => {
        try {
          const type = order.type.charAt(0).toUpperCase() + order.type.substring(1);
          const method = `get${type}Order`;
          const orderFromChain = await contract[method](order.account, order.index);
          const ret: any = {};
          for (const [key, val] of Object.entries(orderFromChain)) {
            ret[key] = val;
          }
          if (order.type === "swap") {
            ret.path = [ret.path0, ret.path1, ret.path2].filter((address) => address !== AddressZero);
          }
          ret.type = type;
          ret.index = order.index;
          ret.account = order.account;
          ret.createdTimestamp = order.createdTimestamp;
          return ret;
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      })
    );
  });

  return orders.filter(Boolean);
}

export function usePositionsForOrders(chainId, library, orders) {
  const key = orders ? orders.map((order) => getOrderKey(order) + "____") : null;
  const { data: positions = {} } = useSWR(key, async () => {
    const provider = getProvider(library, chainId);
    const vaultAddress = getContract(chainId, "Vault");
    const contract = new ethers.Contract(vaultAddress, Vault.abi, provider);
    const data = await Promise.all(
      orders.map(async (order) => {
        try {
          const position = await contract.getPosition(
            order.account,
            order.collateralToken,
            order.indexToken,
            order.isLong
          );
          if (position[0].eq(0)) {
            return [null, order];
          }
          return [position, order];
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      })
    );
    return data.reduce((memo, [position, order]) => {
      memo[getOrderKey(order)] = position;
      return memo;
    }, {});
  });

  return positions;
}

function invariant(condition, errorMsg) {
  if (!condition) {
    throw new Error(errorMsg);
  }
}

export function useTrades(chainId, account, forSingleAccount, afterId) {
  let url =
    account && account.length > 0
      ? `${getServerBaseUrl(chainId)}/trades?address=${account.toLowerCase()}`
      : !forSingleAccount && `${getServerBaseUrl(chainId)}/trades`;

  if (afterId && afterId.length > 0) {
    const urlItem = new URL(url as string);
    urlItem.searchParams.append("after", afterId);
    url = urlItem.toString();
  }

  const { data: trades, mutate: updateTrades } = useSWR(url ? url : null, {
    dedupingInterval: 10000,
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  if (trades && !trades.error) {
    trades.sort((item0, item1) => {
      const data0 = item0;
      const data1 = item1;
      const time0 = parseInt(data0.timestamp);
      const time1 = parseInt(data1.timestamp);
      if (time1 > time0) {
        return 1;
      }
      if (time1 < time0) {
        return -1;
      }

      const block0 = parseInt(data0.blockNumber);
      const block1 = parseInt(data1.blockNumber);

      if (isNaN(block0) && isNaN(block1)) {
        return 0;
      }

      if (isNaN(block0)) {
        return 1;
      }

      if (isNaN(block1)) {
        return -1;
      }

      if (block1 > block0) {
        return 1;
      }

      if (block1 < block0) {
        return -1;
      }

      return 0;
    });
  }

  return { trades, updateTrades };
}

export function useMinExecutionFee(library, active, chainId, infoTokens) {
  const positionRouterAddress = getContract(chainId, "PositionRouter");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");

  const { data: minExecutionFee } = useSWR<BigNumber>([active, chainId, positionRouterAddress, "minExecutionFee"], {
    fetcher: contractFetcher(library, PositionRouter),
  });

  const { data: gasPrice } = useSWR<BigNumber | undefined>(["gasPrice", chainId], {
    fetcher: () => {
      return new Promise(async (resolve, reject) => {
        const provider = getProvider(library, chainId);
        if (!provider) {
          resolve(undefined);
          return;
        }

        try {
          const gasPrice = await provider.getGasPrice();
          resolve(gasPrice);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      });
    },
  });

  let multiplier;

  // if gas prices on Arbitrum are high, the main transaction costs would come from the L2 gas usage
  // for executing positions this is around 65,000 gas
  // if gas prices on Ethereum are high, than the gas usage might be higher, this calculation doesn't deal with that
  // case yet
  if (chainId === ARBITRUM || chainId === ARBITRUM_TESTNET) {
    multiplier = 65000;
  }

  // multiplier for Avalanche is just the average gas usage
  if (chainId === AVALANCHE) {
    multiplier = 700000;
  }
  if (chainId === FANTOM) {
    multiplier = 700000;
  }
  if (chainId === OP) {
    multiplier = 700000;
  }
  if (chainId === BASE) {
    multiplier = 700000;
  }

  let finalExecutionFee = minExecutionFee;

  if (gasPrice && minExecutionFee) {
    const estimatedExecutionFee = gasPrice.mul(multiplier);
    if (estimatedExecutionFee.gt(minExecutionFee)) {
      finalExecutionFee = estimatedExecutionFee;
    }
  }

  const finalExecutionFeeUSD = getUsd(finalExecutionFee, nativeTokenAddress, false, infoTokens);
  const isFeeHigh = finalExecutionFeeUSD?.gt(expandDecimals(getHighExecutionFee(chainId), USD_DECIMALS));
  const errorMessage =
    isFeeHigh &&
    `The network cost to send transactions is high at the moment, please check the "Execution Fee" value before proceeding.`;

  return {
    minExecutionFee: finalExecutionFee,
    minExecutionFeeUSD: finalExecutionFeeUSD,
    minExecutionFeeErrorMessage: errorMessage,
  };
}

export function useStakedGmxSupply(library, active) {
  const gmxAddressArb = getContract(ARBITRUM, "GMX");
  const stakedGmxTrackerAddressArb = getContract(ARBITRUM, "StakedGmxTracker");

  const { data: arbData, mutate: arbMutate } = useSWR<any>(
    [`StakeV2:stakedGmxSupply:${active}`, ARBITRUM, gmxAddressArb, "balanceOf", stakedGmxTrackerAddressArb],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  const gmxAddressAvax = getContract(AVALANCHE, "GMX");
  const stakedGmxTrackerAddressAvax = getContract(AVALANCHE, "StakedGmxTracker");

  const { data: avaxData, mutate: avaxMutate } = useSWR(
    [`StakeV2:stakedGmxSupply:${active}`, AVALANCHE, gmxAddressAvax, "balanceOf", stakedGmxTrackerAddressAvax],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  let data;
  if (arbData && avaxData) {
    data = arbData.add(avaxData);
  }

  const mutate = () => {
    arbMutate();
    avaxMutate();
  };

  return { data, mutate };
}

export function useHasOutdatedUi() {
  // const url = getServerUrl(ARBITRUM, "/ui_version");
  // const { data, mutate } = useSWR([url], {
  //   // @ts-ignore
  //   fetcher: (...args) => fetch(...args).then((res) => res.text()),
  // });

  let hasOutdatedUi = false;

  // if (data && parseFloat(data) > parseFloat(UI_VERSION)) {
  //   hasOutdatedUi = true;
  // }

  return { data: hasOutdatedUi };
}
export function useMMYInfo() {
  const url = "https://api.mummy.finance/api/tokens/info?symbols=MMY&chain=FANTOM";
  const { data: data } = useSWR([url], {
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  return {
    totalSupply: parseValue(data?.MMY?.totalSupply, 18) || 0,
    totalGmxSupply: parseValue(data?.MMY?.circulatingSupply, 18) || 0,
    gmxPrice: parseValue(data?.MMY?.price, USD_DECIMALS) || 0,
  };
}
export function useGmxPrice(chainId, libraries, active) {
  const arbitrumLibrary = libraries && libraries.arbitrum ? libraries.arbitrum : undefined;
  const opLibrary = libraries && libraries.op ? libraries.op : undefined;

  const { data: gmxPriceFromArbitrum, mutate: mutateFromArbitrum } = useGmxPriceFromArbitrum(arbitrumLibrary, active);
  // const { data: gmxPriceFromOP, mutate: mutateFromOP } = useGmxPriceFromOP();
  const { data: gmxPriceFromOP, mutate: mutateFromOP } = useGmxPriceFromOP2(opLibrary, active);
  const { data: gmxPriceFromFantom, mutate: mutateFromFantom } = useGmxPriceFromFantom();
  const { data: gmxPriceFromBase, mutate: mutateFromBase } = useGmxPriceFromFantom();

  let gmxPrice = gmxPriceFromFantom;
  if (chainId === OP) {
    gmxPrice = gmxPriceFromOP;
  }
  if (chainId === ARBITRUM) {
    gmxPrice = gmxPriceFromArbitrum;
  }
  if (chainId === BASE) {
    gmxPrice = gmxPriceFromBase;
  }
  const mutate = useCallback(() => {
    mutateFromFantom();
    mutateFromOP();
    mutateFromArbitrum();
    mutateFromBase()
  }, [mutateFromFantom, mutateFromOP, mutateFromArbitrum, mutateFromBase]);

  return {
    gmxPrice,
    gmxPriceFromArbitrum,
    gmxPriceFromOP,
    gmxPriceFromFantom,
    gmxPriceFromBase,
    mutate,
  };
}
export function useBridgeApi(account, currentTab) {
  const url = `https://api.mummy.finance/layerzero/bridge/histories/${account}${
    currentTab === "MMY" ? "" : "?type=NFT"
  }`;
  const data = useSWR([url], {
    dedupingInterval: 30000,
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  if (data && data?.data) return data?.data;
  else return null;
}
export function useMigrateApi(account, chainId) {
  const url = `https://api.mummy.finance/layerzero/migration/histories/${account}?chainId=${chainId}`;
  const data = useSWR([url], {
    dedupingInterval: 30000,
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  if (data && data?.data) return data?.data;
  else return null;
}
// use only the supply endpoint on arbitrum, it includes the supply on avalanche
export function useTotalGmxSupply() {
  const gmxSupplyUrlArbitrum = getServerUrl(ARBITRUM, "/gmx_supply");

  const { data: gmxSupply, mutate: updateGmxSupply } = useSWR([gmxSupplyUrlArbitrum], {
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.text()),
  });

  return {
    total: gmxSupply ? bigNumberify(gmxSupply) : undefined,
    mutate: updateGmxSupply,
  };
}

export function useTotalGmxSupplyAll() {
  const gmxAddressOP = getContract(OP, "GMX");
  const glpAddressOP = getContract(OP, "GLP");
  const usdgAddressOP = getContract(OP, "USDG");
  const readerAddressOP = getContract(OP, "Reader");
  const tokensForSupplyQueryOP = [gmxAddressOP, glpAddressOP, usdgAddressOP];

  const gmxAddress = getContract(FANTOM, "GMX");
  const glpAddress = getContract(FANTOM, "GLP");
  const usdgAddress = getContract(FANTOM, "USDG");
  const readerAddressFtm = getContract(FANTOM, "Reader");
  const tokensForSupplyQuery = [gmxAddress, glpAddress, usdgAddress];

  const gmxAddressArb = getContract(ARBITRUM, "GMX");
  const glpAddressArb = getContract(ARBITRUM, "GLP");
  const usdgAddressArb = getContract(ARBITRUM, "USDG");
  const readerAddressArb = getContract(ARBITRUM, "Reader");
  const tokensForSupplyQueryArb = [gmxAddressArb, glpAddressArb, usdgAddressArb];

  const gmxAddressBase = getContract(BASE, "GMX");
  const glpAddressBase = getContract(BASE, "GLP");
  const usdgAddressBase = getContract(BASE, "USDG");
  const readerAddressBase = getContract(BASE, "Reader");
  const tokensForSupplyQueryBase = [gmxAddressBase, glpAddressBase, usdgAddressBase];

  let totalSupplies = useRef(bigNumberify(0));


  const { data: totalSuppliesBASE, mutate: updateTotalSuppliesBASE } = useSWR<any>(
    [`Dashboard:totalSuppliesBASE:${BASE}`, BASE, readerAddressBase, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: contractFetcher(undefined, ReaderV2, [tokensForSupplyQueryBase]),
    }
  );

  const { data: totalSuppliesOP, mutate: updateTotalSuppliesOP } = useSWR<any>(
    [`Dashboard:totalSuppliesOP:${OP}`, OP, readerAddressOP, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: contractFetcher(undefined, ReaderV2, [tokensForSupplyQueryOP]),
    }
  );

  const { data: totalSuppliesFtm, mutate: updateTotalSuppliesFtm } = useSWR<any>(
    [`Dashboard:totalSuppliesOP:${FANTOM}`, FANTOM, readerAddressFtm, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: contractFetcher(undefined, ReaderV2, [tokensForSupplyQuery]),
    }
  );
  const { data: mulSuppliesFtm } = useSWR(
    [
      `Bridge:mulSuppliesFtm:${FANTOM}`,
      FANTOM,
      "0x01e77288b38b416F972428d562454fb329350bAc",
      "balanceOf",
      "0x23a2d47Af72A9d18B756DfDDEF790cdc1d0e9005",
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: totalSuppliesArb, mutate: updateTotalSuppliesArb } = useSWR<any>(
    [`Dashboard:totalSuppliesArb:${ARBITRUM}`, ARBITRUM, readerAddressArb, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: contractFetcher(undefined, ReaderV2, [tokensForSupplyQueryArb]),
    }
  );
  const mutate = useCallback(() => {
    updateTotalSuppliesOP();
    updateTotalSuppliesFtm();
    updateTotalSuppliesArb();
    updateTotalSuppliesBASE()
  }, [updateTotalSuppliesOP, updateTotalSuppliesFtm, updateTotalSuppliesArb, updateTotalSuppliesBASE]);

  if (
    totalSuppliesOP &&
    totalSuppliesFtm &&
    totalSuppliesOP[1] &&
    totalSuppliesFtm[1] &&
    totalSuppliesArb &&
    totalSuppliesArb[1] &&
    totalSuppliesBASE &&
    totalSuppliesBASE[1] &&
    mulSuppliesFtm
  ) {
    let total = totalSuppliesFtm[1].add(totalSuppliesOP[1]).add(totalSuppliesArb[1]).add(totalSuppliesBASE[1]).sub(mulSuppliesFtm);
    totalSupplies.current = total;
  }

  return {
    op: totalSuppliesOP && totalSuppliesOP[1] ? totalSuppliesOP[1] : bigNumberify(0),
    fantom:
      mulSuppliesFtm && totalSuppliesFtm && totalSuppliesFtm[1]
        ? totalSuppliesFtm[1].sub(mulSuppliesFtm)
        : bigNumberify(0),
    arb: totalSuppliesArb && totalSuppliesArb[1] ? totalSuppliesArb[1] : bigNumberify(0),
    base: totalSuppliesBASE && totalSuppliesBASE[1] ? totalSuppliesBASE[1] : bigNumberify(0),
    total: totalSupplies.current,
    mutate,
  };
}

export function useTotalGmxStaked() {
  const stakedGmxTrackerAddressArbitrum = getContract(ARBITRUM, "StakedGmxTracker");
  const stakedGmxTrackerAddressBase = getContract(BASE, "StakedGmxTracker");
  const stakedGmxTrackerAddressOP = getContract(OP, "StakedGmxTracker");
  const stakedGmxTrackerAddressFtm = getContract(FANTOM, "StakedGmxTracker");
  let totalStakedGmx = useRef(bigNumberify(0));

  const { data: stakedGmxSupplyBase, mutate: updateStakedGmxSupplyBase } = useSWR<BigNumber>(
    [
      `StakeV2:stakedGmxSupplyBase:${BASE}`,
      BASE,
      getContract(BASE, "GMX"),
      "balanceOf",
      stakedGmxTrackerAddressBase,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyArbitrum, mutate: updateStakedGmxSupplyArbitrum } = useSWR<BigNumber>(
    [
      `StakeV2:stakedGmxSupply:${ARBITRUM}`,
      ARBITRUM,
      getContract(ARBITRUM, "GMX"),
      "balanceOf",
      stakedGmxTrackerAddressArbitrum,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyOP, mutate: updateStakedGmxSupplyOP } = useSWR<BigNumber>(
    [`StakeV2:stakedGmxSupplyOP:${OP}`, OP, getContract(OP, "GMX"), "balanceOf", stakedGmxTrackerAddressOP],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyOPLegacy, mutate: updateStakedGmxSupplyOPLegacy } = useSWR<BigNumber>(
    [
      `StakeV2:stakedGmxSupplyOPLegacy:${OP}`,
      OP,
      "0x47536f17f4ff30e64a96a7555826b8f9e66ec468",
      "balanceOf",
      stakedGmxTrackerAddressOP,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const { data: stakedGmxSupplyFtm, mutate: updateStakedGmxSupplyFtm } = useSWR<BigNumber>(
    [
      `StakeV2:stakedGmxSupplyFtm:${FANTOM}`,
      FANTOM,
      getContract(FANTOM, "GMX"),
      "balanceOf",
      stakedGmxTrackerAddressFtm,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const { data: stakedGmxSupplyFtmLegacy, mutate: updateStakedGmxSupplyFtmLegacy } = useSWR<BigNumber>(
    [
      `StakeV2:stakedGmxSupplyFtm:${FANTOM}`,
      FANTOM,
      getContract(FANTOM, "GMX"),
      "balanceOf",
      "0x727dB8FA7861340d49d13ea78321D0C9a1a79cd5",
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const mutate = useCallback(() => {
    updateStakedGmxSupplyArbitrum();
    updateStakedGmxSupplyOP();
    updateStakedGmxSupplyOPLegacy();
    updateStakedGmxSupplyFtm();
    updateStakedGmxSupplyFtmLegacy();
    updateStakedGmxSupplyBase()
  }, [
    updateStakedGmxSupplyFtmLegacy,
    updateStakedGmxSupplyFtm,
    updateStakedGmxSupplyOP,
    updateStakedGmxSupplyOPLegacy,
    updateStakedGmxSupplyArbitrum,
    updateStakedGmxSupplyBase
  ]);

  if (
    stakedGmxSupplyFtm &&
    stakedGmxSupplyOP &&
    stakedGmxSupplyOPLegacy &&
    stakedGmxSupplyArbitrum &&
    stakedGmxSupplyFtmLegacy &&
    stakedGmxSupplyBase
  ) {
    let total = stakedGmxSupplyFtm
      .add(stakedGmxSupplyOP)
      .add(stakedGmxSupplyOPLegacy)
      .add(stakedGmxSupplyArbitrum)
      .add(stakedGmxSupplyFtmLegacy)
      .add(stakedGmxSupplyBase);
    totalStakedGmx.current = total;
  }

  return {
    op: stakedGmxSupplyOP,
    oplegacy: stakedGmxSupplyOPLegacy,
    fantom: stakedGmxSupplyFtm,
    fantomlegacy: stakedGmxSupplyFtmLegacy,
    arbitrum: stakedGmxSupplyArbitrum,
    base: stakedGmxSupplyBase,
    total: totalStakedGmx.current,
    mutate,
  };
}

export function useTotalEsGmxStaked() {
  const stakedGmxTrackerAddressOP = getContract(OP, "StakedGmxTracker");
  const stakedGmxTrackerAddressFtm = getContract(FANTOM, "StakedGmxTracker");
  const stakedGmxTrackerAddressArbitrum = getContract(ARBITRUM, "StakedGmxTracker");
  const stakedGmxTrackerAddressBase = getContract(BASE, "StakedGmxTracker");
  let totalStakedGmx = useRef(bigNumberify(0));

  const { data: stakedGmxSupplyOP, mutate: updateStakedGmxSupplyOP } = useSWR<BigNumber>(
    [`StakeV2:stakedEsGmxSupplyOP:${OP}`, OP, getContract(OP, "ES_GMX"), "balanceOf", stakedGmxTrackerAddressOP],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyFtm, mutate: updateStakedGmxSupplyFtm } = useSWR<BigNumber>(
    [
      `StakeV2:stakedEsGmxSupplyFtm:${FANTOM}`,
      FANTOM,
      getContract(FANTOM, "ES_GMX"),
      "balanceOf",
      stakedGmxTrackerAddressFtm,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyFtmLegacy, mutate: updateStakedGmxSupplyFtmLegacy } = useSWR<BigNumber>(
    [
      `StakeV2:stakedEsGmxSupplyFtmLegacy:${FANTOM}`,
      FANTOM,
      getContract(FANTOM, "ES_GMX"),
      "balanceOf",
      "0x727dB8FA7861340d49d13ea78321D0C9a1a79cd5",
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: stakedGmxSupplyArbitrum, mutate: updateStakedGmxSupplyArbitrum } = useSWR<BigNumber>(
    [
      `StakeV2:stakedEsGmxSupplyArbitrum:${ARBITRUM}`,
      ARBITRUM,
      getContract(ARBITRUM, "ES_GMX"),
      "balanceOf",
      stakedGmxTrackerAddressArbitrum,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const { data: stakedGmxSupplyBase, mutate: updateStakedGmxSupplyBase } = useSWR<BigNumber>(
    [
      `StakeV2:stakedEsGmxSupplyBase:${BASE}`,
      BASE,
      getContract(BASE, "ES_GMX"),
      "balanceOf",
      stakedGmxTrackerAddressBase,
    ],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const mutate = useCallback(() => {
    updateStakedGmxSupplyArbitrum();
    updateStakedGmxSupplyOP();
    updateStakedGmxSupplyFtm();
    updateStakedGmxSupplyFtmLegacy();
    updateStakedGmxSupplyBase()
  }, [
    updateStakedGmxSupplyArbitrum,
    updateStakedGmxSupplyFtm,
    updateStakedGmxSupplyOP,
    updateStakedGmxSupplyFtmLegacy,
    updateStakedGmxSupplyBase,
  ]);

  if (stakedGmxSupplyFtmLegacy && stakedGmxSupplyFtm && stakedGmxSupplyOP && stakedGmxSupplyArbitrum && stakedGmxSupplyBase) {
    let total = stakedGmxSupplyFtm.add(stakedGmxSupplyOP).add(stakedGmxSupplyArbitrum).add(stakedGmxSupplyFtmLegacy).add(stakedGmxSupplyBase);
    totalStakedGmx.current = total;
  }

  return {
    op: stakedGmxSupplyOP,
    fantom: stakedGmxSupplyFtm,
    fantomlegacy: stakedGmxSupplyFtmLegacy,
    arbitrum: stakedGmxSupplyArbitrum,
    base: stakedGmxSupplyBase,
    total: totalStakedGmx.current,
    mutate,
  };
}
export function useTotalGmxInLiquidity() {
  // let poolAddressArbitrum = getContract(ARBITRUM, "UniswapGmxEthPool");
  // let poolAddressAvax = getContract(AVALANCHE, "TraderJoeGmxAvaxPool");
  let poolAddressFtm = getContract(FANTOM, "UniswapGmxEthPool");
  let poolAddressOP = getContract(OP, "UniswapGmxEthPool");

  let totalGMX = useRef(bigNumberify(0));

  // const { data: gmxInLiquidityOnArbitrum, mutate: mutateGMXInLiquidityOnArbitrum } = useSWR<any>(
  //   [`StakeV2:gmxInLiquidity:${ARBITRUM}`, ARBITRUM, getContract(ARBITRUM, "GMX"), "balanceOf", poolAddressArbitrum],
  //   {
  //     fetcher: contractFetcher(undefined, Token),
  //   }
  // );
  // const { data: gmxInLiquidityOnAvax, mutate: mutateGMXInLiquidityOnAvax } = useSWR<any>(
  //   [`StakeV2:gmxInLiquidity:${AVALANCHE}`, AVALANCHE, getContract(AVALANCHE, "GMX"), "balanceOf", poolAddressAvax],
  //   {
  //     fetcher: contractFetcher(undefined, Token),
  //   }
  // );
  const { data: gmxInLiquidityOnFtm, mutate: mutateGMXInLiquidityOnFtm } = useSWR<any>(
    [`StakeV2:gmxInLiquidity:${FANTOM}`, FANTOM, getContract(FANTOM, "GMX"), "balanceOf", poolAddressFtm],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );
  const { data: gmxInLiquidityOnOP, mutate: mutateGMXInLiquidityOnOP } = useSWR<any>(
    [`StakeV2:gmxInLiquidity:${OP}`, OP, getContract(OP, "GMX"), "balanceOf", poolAddressOP],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  const mutate = useCallback(() => {
    mutateGMXInLiquidityOnFtm();
    mutateGMXInLiquidityOnOP();
  }, [mutateGMXInLiquidityOnFtm, mutateGMXInLiquidityOnOP]);

  if (gmxInLiquidityOnFtm && gmxInLiquidityOnOP) {
    let total = bigNumberify(gmxInLiquidityOnFtm)?.add(gmxInLiquidityOnOP);
    totalGMX.current = total;
  }
  return {
    op: gmxInLiquidityOnOP,
    fantom: gmxInLiquidityOnFtm,
    total: totalGMX.current,
    mutate,
  };
}
export function useLqLocked() {
  const poolAddress = getContract(FANTOM, "UniswapGmxEthPool");
  const liquidityLockerAddress = getContract(FANTOM, "LiquidityLocker");

  const { data, mutate: updateReserves } = useSWR(["TraderJoeGmxFantomReserves", FANTOM, poolAddress, "getReserves"], {
    fetcher: contractFetcher(undefined, UniswapV2),
  });
  const { _reserve0: mmyReserve, _reserve1: usdcReserve }: any = data || {};
  const { data: totalSupply, mutate: updateTotalSupply } = useSWR(["totalSupply", FANTOM, poolAddress, "totalSupply"], {
    fetcher: contractFetcher(undefined, UniswapV2),
  });

  const { data: balance } = useSWR(["balance", FANTOM, poolAddress, "balanceOf"], {
    fetcher: contractFetcher(undefined, UniswapV2, [liquidityLockerAddress]),
  });

  const { data: lockUntil } = useSWR(["lockUntil", FANTOM, liquidityLockerAddress, "lockUntil"], {
    fetcher: contractFetcher(undefined, LiquidityLocker),
  });

  if (balance && usdcReserve && mmyReserve && totalSupply && lockUntil) {
    const pooledUSDC = usdcReserve.mul(balance).div(totalSupply);
    const pooledMMY = mmyReserve.mul(balance).div(totalSupply);
    const mmyPrice = usdcReserve.mul(expandDecimals(1, 18)).div(mmyReserve);

    const pooledMMYDolar = mmyReserve.mul(balance).div(totalSupply).mul(mmyPrice).div(expandDecimals(1, 6));
    const tvl = pooledUSDC.mul(2).div(expandDecimals(1, 6));
    return {
      tvl,
      pooledUSDC,
      pooledMMY,
      pooledMMYDolar,
      lockUntil,
      lockTokenddress: "0x2a6538a456650cd454dcd8f0b4665183dba0bb27",
      timeLockddress: "0x8ac2cc7af1517e6f58a31a43773ca6c80bf3edb9",
      lpTokenAddress: poolAddress,
    };
  }
}

function useGmxPriceFromOP2(library, active) {
  const poolAddress = getContract(OP, "UniswapGmxEthPool");
  const { data: uniPoolSlot0, mutate: updateUniPoolSlot0 } = useSWR<any>(
    [`StakeV2:uniPoolSlot0:${active}`, OP, poolAddress, "slot0"],
    {
      fetcher: contractFetcher(library, UniPool),
    }
  );

  const vaultAddress = getContract(OP, "Vault");
  const ethAddress = getTokenBySymbol(OP, "WETH").address;
  const { data: ethPrice, mutate: updateEthPrice } = useSWR<BigNumber>(
    [`StakeV2:ethPrice:${active}`, OP, vaultAddress, "getMinPrice", ethAddress],
    {
      fetcher: contractFetcher(library, Vault),
    }
  );

  const gmxPrice = useMemo(() => {
    if (uniPoolSlot0 && ethPrice) {
      const tokenA = new UniToken(OP, ethAddress, 18, "SYMBOL", "NAME");

      const gmxAddress = getContract(OP, "GMX");
      const tokenB = new UniToken(OP, gmxAddress, 18, "SYMBOL", "NAME");

      const pool = new Pool(
        tokenA, // tokenA
        tokenB, // tokenB
        10000, // fee
        uniPoolSlot0.sqrtPriceX96, // sqrtRatioX96
        1, // liquidity
        uniPoolSlot0.tick, // tickCurrent
        []
      );

      const poolTokenPrice = pool.priceOf(tokenB).toSignificant(6);
      const poolTokenPriceAmount = parseValue(poolTokenPrice, 18);
      return poolTokenPriceAmount?.mul(ethPrice).div(expandDecimals(1, 18));
    }
  }, [ethPrice, uniPoolSlot0, ethAddress]);

  const mutate = useCallback(() => {
    updateUniPoolSlot0(undefined, true);
    updateEthPrice(undefined, true);
  }, [updateEthPrice, updateUniPoolSlot0]);

  return { data: gmxPrice, mutate };
}

function useGmxPriceFromFantom() {
  const poolAddress = getContract(FANTOM, "UniswapGmxEthPool");

  const { data, mutate: updateReserves } = useSWR(["TraderJoeGmxFantomReserves", FANTOM, poolAddress, "getReserves"], {
    fetcher: contractFetcher(undefined, UniswapV2),
  });
  const { _reserve0: gmxReserve, _reserve1: avaxReserve }: any = data || {};

  const vaultAddress = getContract(FANTOM, "Vault");
  const usdcAddress = getTokenBySymbol(FANTOM, "USDC").address;
  // const { data: usdcPrice, mutate: updateUsdcPrice } = useSWR(
  //   [`StakeV2:usdcPrice`, FANTOM, vaultAddress, "getMinPrice", usdcAddress],
  //   {
  //     fetcher: contractFetcher(undefined, Vault),
  //   }
  // );

  const apiPriceUSDC = `https://api.dexscreener.com/latest/dex/pairs/fantom/0x2b4C76d0dc16BE1C31D4C1DC53bF9B45987Fc75c`;

  const { data: usdcData, mutate: updateUsdcPrice } = useSWR([apiPriceUSDC], {
    // @ts-ignore
    fetcher: (...args) => fetch(...args).then((res) => res.json()),
  });
  let usdcPrice = usdcData ? bigNumberify(usdcData.pair.priceUsd * 1000000) : undefined;
  usdcPrice = usdcPrice?.mul(bigNumberify(10)!.pow(30 - 6));
  const PRECISION = bigNumberify(10)!.pow(18);
  const PRECISION_USDC = bigNumberify(10)!.pow(6);
  let gmxPrice;
  if (avaxReserve && gmxReserve && usdcPrice) {
    gmxPrice = avaxReserve.mul(PRECISION).div(gmxReserve).mul(usdcPrice)!.div(PRECISION_USDC);
  }

  const mutate = useCallback(() => {
    updateReserves(undefined, true);
    updateUsdcPrice(undefined, true);
  }, [updateReserves, updateUsdcPrice]);

  return { data: gmxPrice, mutate };
}

function useGmxPriceFromOP() {
  const poolAddress = getContract(OP, "TraderJoeGmxAvaxPool");

  const { data, mutate: updateReserves } = useSWR(["TraderJoeGmxAvaxReserves", OP, poolAddress, "getReserves"], {
    fetcher: contractFetcher(undefined, UniswapV2),
  });
  const { _reserve0: avaxReserve, _reserve1: gmxReserve }: any = data || {};
  const vaultAddress = getContract(OP, "Vault");
  const ethAddress = getTokenBySymbol(OP, "WETH").address;
  const { data: ethPrice, mutate: updateEthPrice } = useSWR(
    [`StakeV2:ethPrice`, OP, vaultAddress, "getMinPrice", ethAddress],
    {
      fetcher: contractFetcher(undefined, Vault),
    }
  );

  const PRECISION = bigNumberify(10)!.pow(18);
  let gmxPrice;
  if (avaxReserve && gmxReserve && ethPrice) {
    gmxPrice = avaxReserve.mul(PRECISION).div(gmxReserve).mul(ethPrice).div(PRECISION);
  }

  const mutate = useCallback(() => {
    updateReserves(undefined, true);
    updateEthPrice(undefined, true);
  }, [updateReserves, updateEthPrice]);

  return { data: gmxPrice, mutate };
}

function useGmxPriceFromArbitrum(library, active) {
  const poolAddress = getContract(ARBITRUM, "UniswapGmxEthPool");
  const { data: uniPoolSlot0, mutate: updateUniPoolSlot0 } = useSWR<any>(
    [`StakeV2:uniPoolSlot0:${active}`, ARBITRUM, poolAddress, "slot0"],
    {
      fetcher: contractFetcher(library, UniPool),
    }
  );

  const vaultAddress = getContract(ARBITRUM, "Vault");
  const ethAddress = getTokenBySymbol(ARBITRUM, "WETH").address;
  const { data: ethPrice, mutate: updateEthPrice } = useSWR<BigNumber>(
    [`StakeV2:ethPrice:${active}`, ARBITRUM, vaultAddress, "getMinPrice", ethAddress],
    {
      fetcher: contractFetcher(library, Vault),
    }
  );

  const gmxPrice = useMemo(() => {
    if (uniPoolSlot0 && ethPrice) {
      const tokenA = new UniToken(ARBITRUM, ethAddress, 18, "SYMBOL", "NAME");

      const gmxAddress = getContract(ARBITRUM, "GMX");
      const tokenB = new UniToken(ARBITRUM, gmxAddress, 18, "SYMBOL", "NAME");

      const pool = new Pool(
        tokenA, // tokenA
        tokenB, // tokenB
        10000, // fee
        uniPoolSlot0.sqrtPriceX96, // sqrtRatioX96
        1, // liquidity
        uniPoolSlot0.tick, // tickCurrent
        []
      );

      const poolTokenPrice = pool.priceOf(tokenB).toSignificant(6);
      const poolTokenPriceAmount = parseValue(poolTokenPrice, 18);
      return poolTokenPriceAmount?.mul(ethPrice).div(expandDecimals(1, 18));
    }
  }, [ethPrice, uniPoolSlot0, ethAddress]);

  const mutate = useCallback(() => {
    updateUniPoolSlot0(undefined, true);
    updateEthPrice(undefined, true);
  }, [updateEthPrice, updateUniPoolSlot0]);

  return { data: gmxPrice, mutate };
}

export async function approvePlugin(chainId, pluginAddress, { library, setPendingTxns, sentMsg, failMsg }) {
  const routerAddress = getContract(chainId, "Router");
  const contract = new ethers.Contract(routerAddress, Router.abi, library.getSigner());
  return callContract(chainId, contract, "approvePlugin", [pluginAddress], {
    sentMsg,
    failMsg,
    setPendingTxns,
  });
}

export async function createSwapOrder(
  chainId,
  library,
  path,
  amountIn,
  minOut,
  triggerRatio,
  nativeTokenAddress,
  opts: any = {}
) {
  const executionFee = getConstant(chainId, "SWAP_ORDER_EXECUTION_GAS_FEE");
  const triggerAboveThreshold = false;
  let shouldWrap = false;
  let shouldUnwrap = false;
  opts.value = executionFee;

  if (path[0] === AddressZero) {
    shouldWrap = true;
    opts.value = opts.value.add(amountIn);
  }
  if (path[path.length - 1] === AddressZero) {
    shouldUnwrap = true;
  }
  path = replaceNativeTokenAddress(path, nativeTokenAddress);

  const params = [path, amountIn, minOut, triggerRatio, triggerAboveThreshold, executionFee, shouldWrap, shouldUnwrap];

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createSwapOrder", params, opts);
}

export async function createIncreaseOrder(
  chainId,
  library,
  nativeTokenAddress,
  path,
  amountIn,
  indexTokenAddress,
  minOut,
  sizeDelta,
  collateralTokenAddress,
  isLong,
  triggerPrice,
  opts: any = {}
) {
  invariant(!isLong || indexTokenAddress === collateralTokenAddress, "invalid token addresses");
  invariant(indexTokenAddress !== AddressZero, "indexToken is 0");
  invariant(collateralTokenAddress !== AddressZero, "collateralToken is 0");

  const fromETH = path[0] === AddressZero;

  path = replaceNativeTokenAddress(path, nativeTokenAddress);
  const shouldWrap = fromETH;
  const triggerAboveThreshold = !isLong;
  const executionFee = getConstant(chainId, "INCREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    path,
    amountIn,
    indexTokenAddress,
    minOut,
    sizeDelta,
    collateralTokenAddress,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
    executionFee,
    shouldWrap,
  ];

  if (!opts.value) {
    opts.value = fromETH ? amountIn.add(executionFee) : executionFee;
  }

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createIncreaseOrder", params, opts);
}

export async function createDecreaseOrder(
  chainId,
  library,
  indexTokenAddress,
  sizeDelta,
  collateralTokenAddress,
  collateralDelta,
  isLong,
  triggerPrice,
  triggerAboveThreshold,
  opts: any = {}
) {
  invariant(!isLong || indexTokenAddress === collateralTokenAddress, "invalid token addresses");
  invariant(indexTokenAddress !== AddressZero, "indexToken is 0");
  invariant(collateralTokenAddress !== AddressZero, "collateralToken is 0");

  const executionFee = getConstant(chainId, "DECREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    indexTokenAddress,
    sizeDelta,
    collateralTokenAddress,
    collateralDelta,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
  ];
  opts.value = executionFee;
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, "createDecreaseOrder", params, opts);
}

export async function cancelSwapOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelDecreaseOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelIncreaseOrder(chainId, library, index, opts) {
  const params = [index];
  const method = "cancelIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export function handleCancelOrder(chainId, library, order, opts) {
  let func;
  if (order.type === SWAP) {
    func = cancelSwapOrder;
  } else if (order.type === INCREASE) {
    func = cancelIncreaseOrder;
  } else if (order.type === DECREASE) {
    func = cancelDecreaseOrder;
  }

  return func(chainId, library, order.index, {
    successMsg: `Order cancelled.`,
    failMsg: `Cancel failed.`,
    sentMsg: `Cancel submitted.`,
    pendingTxns: opts.pendingTxns,
    setPendingTxns: opts.setPendingTxns,
  });
}

export async function cancelMultipleOrders(chainId, library, allIndexes = [], opts) {
  const ordersWithTypes = groupBy(allIndexes, (v: any) => v.split("-")[0]);
  function getIndexes(key) {
    if (!ordersWithTypes[key]) return;
    return ordersWithTypes[key].map((d) => d.split("-")[1]);
  }
  // params order => swap, increase, decrease
  const params = ["Swap", "Increase", "Decrease"].map((key) => getIndexes(key) || []);
  const method = "cancelMultiple";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());
  return callContract(chainId, contract, method, params, opts);
}

export async function updateDecreaseOrder(
  chainId,
  library,
  index,
  collateralDelta,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, collateralDelta, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function updateIncreaseOrder(
  chainId,
  library,
  index,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function updateSwapOrder(chainId, library, index, minOut, triggerRatio, triggerAboveThreshold, opts) {
  const params = [index, minOut, triggerRatio, triggerAboveThreshold];
  const method = "updateSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, library.getSigner());

  return callContract(chainId, contract, method, params, opts);
}

export async function _executeOrder(chainId, library, method, account, index, feeReceiver, opts) {
  const params = [account, index, feeReceiver];
  const positionManagerAddress = getContract(chainId, "PositionManager");
  const contract = new ethers.Contract(positionManagerAddress, PositionManager.abi, library.getSigner());
  return callContract(chainId, contract, method, params, opts);
}

export function executeSwapOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeSwapOrder", account, index, feeReceiver, opts);
}

export function executeIncreaseOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeIncreaseOrder", account, index, feeReceiver, opts);
}

export function executeDecreaseOrder(chainId, library, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, library, "executeDecreaseOrder", account, index, feeReceiver, opts);
}
